// Email capture for any site. The list lives in our own database (POST /api/subscribe), so it is
// portable to any sending provider later and belongs to us either way. Single field, one click,
// visible promise: anything longer costs subscribers.
import { useState } from 'react';
import { useBotTrap } from '@/lib/botTrap';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SubscribeBox({
  site = 'vikasifications',
  source = '',
  heading = 'Get the build log',
  promise = 'What I shipped, what broke, and what it cost. Roughly weekly, no spam, one-click unsubscribe.',
  className = '',
  variant = 'light',
}) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [msg, setMsg] = useState('');
  const { trapField, trapMeta } = useBotTrap();
  // Someone who has already joined this site's list is not asked again, on any page of that site.
  const doneKey = `amm_subscribed_${site}`;
  const [already] = useState(() => { try { return !!localStorage.getItem(doneKey); } catch { return false; } });

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending' || !email.trim()) return;
    setState('sending');
    try {
      const r = await fetch(`${API}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), site, source: source || site, ...trapMeta() }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        try { localStorage.setItem(doneKey, '1'); } catch { /* ignore */ }
        setState('done');
        setMsg(d.already ? 'You were already on the list.' : 'Done. Check your inbox for the first one.');
        setEmail('');
      } else {
        setState('error');
        setMsg(d.detail || 'That did not work. Try again?');
      }
    } catch {
      setState('error');
      setMsg('Network error. Try again in a moment.');
    }
  };

  const dark = variant === 'dark';
  if (already && state === 'idle') return null;
  return (
    <div className={`rounded-xl border p-6 ${dark ? 'bg-black border-black/80' : 'bg-gray-50 border-gray-200'} ${className}`} data-testid="subscribe-box">
      <p className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-white/60' : 'text-black'}`}>{heading}</p>
      <p className={`text-sm mt-2 leading-relaxed ${dark ? 'text-white/80' : 'text-black'}`}>{promise}</p>
      {state === 'done' ? (
        <p className={`text-sm mt-4 font-medium ${dark ? 'text-white' : 'text-black'}`} data-testid="subscribe-done">{msg}</p>
      ) : (
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2 relative">
          {trapField}
          <label htmlFor="subscribe-email" className="sr-only">Email address</label>
          <input
            id="subscribe-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={`flex-1 h-11 px-3 rounded-lg text-sm outline-none border ${dark
              ? 'bg-white/10 border-white/20 text-white placeholder-white/40 focus:border-white/50'
              : 'bg-white border-gray-300 text-black placeholder-gray-400 focus:border-black'}`}
          />
          <button
            type="submit"
            disabled={state === 'sending'}
            className={`h-11 px-5 rounded-lg text-sm font-semibold disabled:opacity-50 ${dark
              ? 'bg-white text-black hover:bg-white/90' : 'bg-black text-white hover:bg-black/85'}`}
            data-testid="subscribe-submit"
          >
            {state === 'sending' ? 'Adding…' : 'Subscribe'}
          </button>
        </form>
      )}
      {state === 'error' && <p className="text-sm mt-2 text-red-600">{msg}</p>}
    </div>
  );
}
